import { RadioGroup } from '@headlessui/react';
import { useState } from 'react';

import { type SubscriberPreference, SubscriberPreferenceDisplayType } from '~/interfaces/subscriber_preferences';

import CheckboxOption from './CheckboxOption';
import NumeralOption from './NumeralOption';
import PreferenceDescription from './PreferenceDescription';
import PreferenceName from './PreferenceName';
import RadioOption from './RadioOption';

interface Props {
  preference: SubscriberPreference;
  onChange: (value: string | string[]) => void;
}

const MultipleChoiceField = ({ preference, onChange }: Props) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(
    preference.display_type === SubscriberPreferenceDisplayType.RADIO_BUTTONS ? preference.value || null : null,
  );

  const [selectedValues, setSelectedValues] = useState<string[]>(
    [
      SubscriberPreferenceDisplayType.CHECKBOXES,
      SubscriberPreferenceDisplayType.NUMERALS,
      SubscriberPreferenceDisplayType.UNSTYLED,
    ].includes(preference.display_type)
      ? typeof preference.value === 'string'
        ? preference.value.split('||')
        : []
      : [],
  );

  const handleChangeInValue = (value: string) => {
    setSelectedValue(value);
    onChange(value);
  };

  const handleChangeInValues = (value: string, _checked: boolean) => {
    if (selectedValues.includes(value)) {
      const newValue = [...selectedValues].filter((filteredValue) => filteredValue !== value);
      setSelectedValues(newValue);
      onChange(newValue.join('||'));
    } else {
      const newValue = [...selectedValues, value];
      setSelectedValues(newValue);
      onChange(newValue.join('||'));
    }
  };

  return (
    <div className="flex flex-col gap-x-4 gap-y-2">
      <div className="flex flex-col gap-y-0.5">
        <PreferenceName name={preference.name} />
        <PreferenceDescription description={preference.description} />
      </div>
      {preference.display_type === SubscriberPreferenceDisplayType.RADIO_BUTTONS ? (
        <RadioGroup value={selectedValue} onChange={handleChangeInValue}>
          <ul className="space-y-1">
            {preference.subscriber_preference_options.map((option) => {
              const isSelected = selectedValue === option.value;
              return (
                <li key={option.id}>
                  <RadioGroup.Option value={option.value}>
                    <RadioOption value={option.value} isSelected={isSelected} />
                  </RadioGroup.Option>
                </li>
              );
            })}
          </ul>
        </RadioGroup>
      ) : null}
      {[
        SubscriberPreferenceDisplayType.CHECKBOXES,
        SubscriberPreferenceDisplayType.NUMERALS,
        SubscriberPreferenceDisplayType.UNSTYLED,
      ].includes(preference.display_type) ? (
        <ul className="space-y-1">
          {preference.subscriber_preference_options.map((option, index) => {
            const isSelected = selectedValues.includes(option.value);

            switch (preference.display_type) {
              case SubscriberPreferenceDisplayType.CHECKBOXES:
                return (
                  <li key={option.id}>
                    <CheckboxOption
                      name={`${preference.id}-${option.id}`}
                      value={option.value}
                      isSelected={isSelected}
                      onChange={handleChangeInValues}
                    />
                  </li>
                );
              case SubscriberPreferenceDisplayType.NUMERALS:
                return (
                  <li key={option.id}>
                    <NumeralOption
                      name={`${preference.id}-${option.id}`}
                      value={option.value}
                      isSelected={isSelected}
                      onChange={handleChangeInValues}
                      index={index}
                    />
                  </li>
                );
              case SubscriberPreferenceDisplayType.UNSTYLED:
                return (
                  <li key={option.id}>
                    <NumeralOption
                      name={`${preference.id}-${option.id}`}
                      value={option.value}
                      isSelected={isSelected}
                      onChange={handleChangeInValues}
                    />
                  </li>
                );
              default:
                return null;
            }
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default MultipleChoiceField;
